import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles, Check, ArrowRight, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useAuth } from '../App';
import EmailVerifyGate from './EmailVerifyGate';

const PERKS = [
  'Unlock all themes, fonts & wallpapers',
  'Tip Jar (PayPal payouts)',
  'Fan Page + Visitor analytics',
  'Remove LinkMonkey branding',
];

export const UpgradeModal = ({ open, onClose, feature = 'this feature' }) => {
  const { user, refreshUser } = useAuth() || {};
  const [cycle, setCycle] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);

  if (!open) return null;

  const canStartTrial = user && !user?.subscription?.is_pro && !user?.trial_used;

  const handleUpgrade = async () => {
    if (user?.email_verified === false) {
      toast.error('Please verify your email before upgrading. Check your inbox for the verification link.');
      return;
    }
    setLoading(true);
    try {
      const { url } = await api.createCheckout(cycle);
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'Checkout failed');
      setLoading(false);
    }
  };

  const handleTrial = async () => {
    if (user?.email_verified === false) {
      toast.error('Please verify your email before starting your trial. Check your inbox for the verification link.');
      return;
    }
    setTrialLoading(true);
    try {
      await api.startTrial();
      toast.success('Your 7-day Pro trial is active!');
      if (refreshUser) await refreshUser();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Could not start trial');
      setTrialLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose} data-testid="upgrade-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0F0F12] text-zinc-100 p-7 shadow-2xl"
        style={{ fontFamily: "'Inter', sans-serif" }}>
        <button onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
          data-testid="upgrade-modal-close">
          <X className="w-4 h-4" />
        </button>

        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-black bg-white px-2.5 py-0.5 rounded-full mb-4">
          <Sparkles className="w-3 h-3" /> Pro
        </div>

        <h3 className="text-2xl font-semibold tracking-tight text-white mb-1.5">
          Unlock {feature}
        </h3>
        <p className="text-[14px] text-zinc-400 mb-5">
          Pro gives you everything LinkMonkey can do, for less than a coffee.
        </p>

        <div className="space-y-2 mb-6">
          {PERKS.map((p, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <span className="text-[13.5px] text-zinc-300">{p}</span>
            </div>
          ))}
        </div>

        {/* Email verification gate (renders only when user.email_verified === false) */}
        <EmailVerifyGate user={user} theme="modal" />

        {/* Cycle toggle */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button onClick={() => setCycle('monthly')}
            className={`p-3 rounded-lg text-left border transition-all ${cycle === 'monthly' ? 'border-white/30 bg-white/[0.05]' : 'border-white/10 hover:border-white/20'}`}
            data-testid="modal-toggle-monthly">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Monthly</p>
            <p className="text-lg font-semibold text-white">€4<span className="text-zinc-500 text-sm font-normal">/mo</span></p>
          </button>
          <button onClick={() => setCycle('yearly')}
            className={`p-3 rounded-lg text-left border transition-all relative ${cycle === 'yearly' ? 'border-white/30 bg-white/[0.05]' : 'border-white/10 hover:border-white/20'}`}
            data-testid="modal-toggle-yearly">
            <span className="absolute top-2 right-2 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">-17%</span>
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Yearly</p>
            <p className="text-lg font-semibold text-white">€3.33<span className="text-zinc-500 text-sm font-normal">/mo</span></p>
          </button>
        </div>

        <button onClick={handleUpgrade} disabled={loading}
          className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-white text-black px-5 py-2.5 rounded-md hover:bg-zinc-200 transition-colors disabled:opacity-60"
          data-testid="modal-upgrade-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Subscribe — €{cycle === 'yearly' ? '40/yr' : '4/mo'} <ArrowRight className="w-4 h-4" /></>}
        </button>
        <p className="text-[11px] text-zinc-500 text-center mt-1.5">Auto-renews. Cancel anytime in one click.</p>

        {canStartTrial && (
          <>
            <div className="flex items-center gap-3 my-3 text-[11px] text-zinc-600">
              <span className="flex-1 h-px bg-white/[0.06]" />
              <span>or</span>
              <span className="flex-1 h-px bg-white/[0.06]" />
            </div>
            <button onClick={handleTrial} disabled={trialLoading}
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-zinc-300 hover:text-white border border-white/[0.10] hover:border-white/[0.20] hover:bg-white/[0.03] px-5 py-2.5 rounded-md transition-all disabled:opacity-60"
              data-testid="modal-trial-btn">
              {trialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Clock className="w-4 h-4" /> Start 7-day free trial</>}
            </button>
            <p className="text-[11px] text-zinc-500 text-center mt-1.5">No card required.</p>
          </>
        )}

        <Link to="/pricing" onClick={onClose}
          className="block text-center text-[12px] text-zinc-500 hover:text-zinc-300 mt-3 transition-colors">
          See full plan comparison →
        </Link>
      </div>
    </div>
  );
};

export default UpgradeModal;
